import type { InfiniteData, QueryClient } from '@tanstack/react-query';
import type {
  FonotekaPack,
  FonotekaPackSummaryPage,
  FonotekaTrack,
  FonotekaTrackLite,
  Project,
  ProjectFonotekaTrack,
  ProjectFonotekaTracks,
} from '@/api/client';
import { patchProjectListCaches } from '@/lib/projectListQueryCache';
import { setProjectEntity } from '@/lib/projectEntityCache';
import {
  FONOTEKA_LAZY_QUERY_ROOT,
  FONOTEKA_QUERY_KEY,
  fonotekaPackTracksKey,
  PROJECT_FONOTEKA_TRACKS_ROOT,
  projectFonotekaTracksKey,
} from './queryKeys';
import { compareFonotekaPacks, compareFonotekaTracks } from './sortCatalog';
import { fonotekaTrackLite } from './trackLite';

type FonotekaPackDeleted = { id: string; deleted: true };
type FonotekaTrackDeleted = { id: string; pack_id: string; deleted: true };

function patchProjectFieldInCaches(
  queryClient: QueryClient,
  projectId: string,
  patch: (project: Project) => Project
): void {
  // patch вернул тот же проект — значение не изменилось: кэши не трогаем, карточка не перерисовывается
  patchProjectListCaches(queryClient, (prev) => {
    if (!prev) return prev;
    const idx = prev.findIndex((p) => p.id === projectId);
    if (idx < 0) return prev;
    const updated = patch(prev[idx]);
    return updated === prev[idx] ? prev : prev.map((p) => (p.id === projectId ? updated : p));
  });
  const entity = queryClient.getQueryData<Project>(['project', projectId]);
  if (entity) {
    const next = patch(entity);
    if (next === entity) return;
    queryClient.setQueryData(['project', projectId], next);
    setProjectEntity(queryClient, next);
  }
}

export function patchFonotekaPackInCache(
  queryClient: QueryClient,
  payload: FonotekaPack | FonotekaPackDeleted
): void {
  queryClient.setQueryData<FonotekaPack[]>(FONOTEKA_QUERY_KEY, (prev) => {
    if (!prev) return prev;

    if ('deleted' in payload && payload.deleted) {
      const next = prev.filter((pack) => pack.id !== payload.id);
      return next.length === prev.length ? prev : next;
    }

    const pack = payload as FonotekaPack;
    const idx = prev.findIndex((p) => p.id === pack.id);
    if (idx < 0) {
      return [...prev, pack].sort(compareFonotekaPacks);
    }
    const next = [...prev];
    next[idx] = pack;
    return next.sort(compareFonotekaPacks);
  });
}

export function patchFonotekaTrackInCache(
  queryClient: QueryClient,
  payload: FonotekaTrack | FonotekaTrackDeleted
): void {
  queryClient.setQueryData<FonotekaPack[]>(FONOTEKA_QUERY_KEY, (prev) => {
    if (!prev) return prev;

    if ('deleted' in payload && payload.deleted) {
      let changed = false;
      const next = prev.map((pack) => {
        if (pack.id !== payload.pack_id) return pack;
        const tracks = pack.tracks.filter((track) => track.id !== payload.id);
        if (tracks.length === pack.tracks.length) return pack;
        changed = true;
        return { ...pack, tracks };
      });
      return changed ? next : prev;
    }

    const track = payload as FonotekaTrack;
    let changed = false;
    const next = prev.map((pack) => {
      if (pack.id !== track.pack_id) return pack;
      const idx = pack.tracks.findIndex((t) => t.id === track.id);
      const tracks =
        idx < 0
          ? [...pack.tracks, track].sort(compareFonotekaTracks)
          : pack.tracks.map((t) => (t.id === track.id ? track : t)).sort(compareFonotekaTracks);
      changed = true;
      return { ...pack, tracks };
    });
    return changed ? next : prev;
  });
}

export function patchProjectFonotekaCount(
  queryClient: QueryClient,
  projectId: string,
  count: number
): void {
  patchProjectFieldInCaches(queryClient, projectId, (project) =>
    project.fonoteka_track_count === count ? project : { ...project, fonoteka_track_count: count }
  );
}

function sameFields<T extends object>(a: T, b: T): boolean {
  const keys = Object.keys(a) as (keyof T)[];
  return keys.length === Object.keys(b).length && keys.every((k) => a[k] === b[k]);
}

/**
 * Новый список треков поверх прежнего без лишних перерисовок: трек с теми же полями остаётся прежним объектом, прежние —
 * в прежнем порядке, новые — в конце; ничего не изменилось — возвращается прежний список.
 */
export function reconcileProjectTracks(prev: readonly ProjectFonotekaTrack[], next: readonly ProjectFonotekaTrack[]): ProjectFonotekaTrack[] {
  const byId = new Map(next.map((t) => [t.id, t]));
  const out: ProjectFonotekaTrack[] = [];
  for (const old of prev) {
    const fresh = byId.get(old.id);
    if (!fresh) continue;
    out.push(sameFields(old, fresh) ? old : fresh);
    byId.delete(old.id);
  }
  out.push(...byId.values());
  return out.length === prev.length && out.every((t, i) => t === prev[i]) ? (prev as ProjectFonotekaTrack[]) : out;
}

/**
 * Треки проекта сменились. С tracks (ответ сервера) — они, совпавшие с прежними остаются прежними объектами. Только id
 * (realtime, старый выбор по каталогу): известные треки остаются, ушедшие убираются; появились незнакомые — список проекта
 * перечитывается (один маленький запрос). Список не изменился — кэш не трогается.
 */
export function patchProjectFonotekaTracksInCache(
  queryClient: QueryClient,
  projectId: string,
  track_ids: string[],
  tracks?: ProjectFonotekaTrack[]
): void {
  const key = projectFonotekaTracksKey(projectId);
  const prev = queryClient.getQueryData<ProjectFonotekaTracks>(key);
  const known = new Map((prev?.tracks ?? []).map((t) => [t.id, t]));
  const next = tracks ?? track_ids.flatMap((id) => known.get(id) ?? []);
  const reconciled = prev ? reconcileProjectTracks(prev.tracks, next) : next;
  // Только id: незнакомых треков ещё нет в tracks, но id уже в track_ids (по ним старое окно выбора ставит галочки)
  const ids = (tracks ? reconciled.map((t) => t.id) : [...track_ids]).sort();
  const unchanged = prev && reconciled === prev.tracks && ids.length === prev.track_ids.length && ids.every((id, i) => id === prev.track_ids[i]);
  // Только id и списка в кэше нет — показать нечего: пустой список треков в кэше выдал бы себя за загруженный
  if ((prev || tracks) && !unchanged) {
    queryClient.setQueryData<ProjectFonotekaTracks>(key, { track_ids: ids, tracks: reconciled });
  }
  if (!tracks && prev && next.length !== track_ids.length) void queryClient.invalidateQueries({ queryKey: key, exact: true });
  patchProjectFonotekaCount(queryClient, projectId, track_ids.length);
}

const trackCollator = new Intl.Collator('ru', { sensitivity: 'base', numeric: true });
const byFileName = (a: FonotekaTrackLite, b: FonotekaTrackLite) => trackCollator.compare(a.file_name, b.file_name) || (a.id < b.id ? -1 : 1);

type SummaryPages = InfiniteData<FonotekaPackSummaryPage, number>;

function patchSummaries(queryClient: QueryClient, patch: (pages: SummaryPages) => SummaryPages): void {
  for (const [key, data] of queryClient.getQueriesData<SummaryPages>({ queryKey: [...FONOTEKA_LAZY_QUERY_ROOT, 'summaries'] })) {
    if (!data) continue;
    const next = patch(data);
    if (next !== data) queryClient.setQueryData(key, next);
  }
}

function mapSummaryItems(pages: SummaryPages, map: (item: FonotekaPackSummaryPage['items'][number]) => FonotekaPackSummaryPage['items'][number] | null): SummaryPages {
  let changed = false;
  const nextPages = pages.pages.map((page) => {
    const items = page.items.flatMap((item) => {
      const next = map(item);
      if (next !== item) changed = true;
      return next ? [next] : [];
    });
    return items.length === page.items.length && items.every((it, i) => it === page.items[i]) ? page : { ...page, items };
  });
  return changed ? { ...pages, pages: nextPages } : pages;
}

/**
 * Трек или проект Фонотеки изменили (переименовали, удалили, загрузили) — у всех открытых карточек и окон выбора, точечно:
 * в треках проектов — название файла, проект Фонотеки, удалённый уходит; в окне выбора — трек в раскрытом проекте Фонотеки
 * и в найденных, число треков проекта Фонотеки. Окно выбора не перечитывается: пока оно открыто, порядок проектов Фонотеки
 * не меняется (свежий порядок — при следующем открытии). Новый проект Фонотеки в открытое окно не добавляется — по той же причине.
 */
export function patchLazyFonotekaCaches(
  queryClient: QueryClient,
  change:
    | { kind: 'track'; track: FonotekaTrack | { id: string; pack_id?: string; deleted: true } }
    | { kind: 'pack'; pack: FonotekaPack | { id: string; deleted: true } }
): void {
  for (const [key, data] of queryClient.getQueriesData<ProjectFonotekaTracks>({ queryKey: PROJECT_FONOTEKA_TRACKS_ROOT })) {
    if (!data?.tracks) continue;
    let next: ProjectFonotekaTrack[];
    if (change.kind === 'track') {
      const t = change.track;
      next = 'deleted' in t ? data.tracks.filter((x) => x.id !== t.id) : data.tracks.map((x) => (x.id === t.id ? { ...x, ...fonotekaTrackLite(t) } : x));
    } else {
      const p = change.pack;
      next = 'deleted' in p ? data.tracks.filter((x) => x.pack_id !== p.id) : data.tracks.map((x) => (x.pack_id === p.id ? { ...x, pack_name: p.name, pack_project_id: p.project_id } : x));
    }
    const tracks = reconcileProjectTracks(data.tracks, next);
    if (tracks !== data.tracks) queryClient.setQueryData<ProjectFonotekaTracks>(key, { track_ids: tracks.map((t) => t.id).sort(), tracks });
  }

  if (change.kind === 'pack') {
    const p = change.pack;
    if ('deleted' in p) {
      queryClient.removeQueries({ queryKey: fonotekaPackTracksKey(p.id), exact: true });
      patchSummaries(queryClient, (pages) => mapSummaryItems(pages, (item) => (item.id === p.id ? null : item)));
    } else {
      patchSummaries(queryClient, (pages) =>
        mapSummaryItems(pages, (item) => (item.id === p.id && (item.name !== p.name || item.project_id !== p.project_id) ? { ...item, name: p.name, project_id: p.project_id } : item))
      );
    }
    return;
  }

  const t = change.track;
  const packId = t.pack_id;
  if (!packId) return;
  // Трек появился или ушёл — видно по загруженным трекам его проекта Фонотеки; не раскрывали — число обновится при открытии окна
  let delta = 0;
  const packKey = fonotekaPackTracksKey(packId);
  const loaded = queryClient.getQueryData<FonotekaTrackLite[]>(packKey);
  if (loaded) {
    const idx = loaded.findIndex((x) => x.id === t.id);
    let next = loaded;
    if ('deleted' in t) {
      if (idx >= 0) {
        next = loaded.filter((x) => x.id !== t.id);
        delta = -1;
      }
    } else {
      const lite = fonotekaTrackLite(t);
      if (idx < 0) {
        next = [...loaded, lite].sort(byFileName);
        delta = 1;
      } else if (!sameFields(loaded[idx]!, lite)) {
        next = loaded.map((x) => (x.id === t.id ? lite : x));
      }
    }
    if (next !== loaded) queryClient.setQueryData(packKey, next);
  }
  patchSummaries(queryClient, (pages) =>
    mapSummaryItems(pages, (item) => {
      if (item.id !== packId) return item;
      let matched = item.matched_tracks;
      if (matched && matched.some((x) => x.id === t.id)) {
        if ('deleted' in t) matched = matched.filter((x) => x.id !== t.id);
        else {
          const lite = fonotekaTrackLite(t);
          matched = matched.map((x) => (x.id === t.id && !sameFields(x, lite) ? lite : x));
          if (matched.every((x, i) => x === item.matched_tracks![i])) matched = item.matched_tracks;
        }
      }
      if (delta === 0 && matched === item.matched_tracks) return item;
      const track_count = item.track_count + delta;
      return track_count <= 0 ? null : { ...item, track_count, matched_tracks: matched };
    })
  );
}
